import {
  Component,
  ViewEncapsulation,
  DestroyRef,
  ElementRef,
  input,
  output,
  signal,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import * as _ from 'lodash';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  IMultiSelectSettings,
  IMultiSelectTexts,
} from '@/app/core/dropdown/types';
import { HeaderService } from './header.service';
import { AlertNotificationService } from './alert-notification.service';
import { Alert, AlertStatus } from '@/app/models/alert';
import { ExtendedMultiSelectOption } from '@/app/models/extended-multiselect-option';
import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { ActiveFilterService } from '@/app/core/query/active-filter.service';
import { CoreModule } from '@/app/core/core.module';

declare let $: any;

export interface AlertCountInfo {
  alertCount: number;
  alertReadCount: number;
  totalAlerts: number;
}

/**
 * TODO: This component was stripped out of the header.component.ts.  There is a lot of cleanup in the css and html that needs to be done.
 */

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[rdo-alert-menu]',
  templateUrl: 'alert-menu.component.html',
  styleUrls: ['alert-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InfiniteScrollModule,
    CoreModule,
    TranslateModule,
  ],
  host: {
    class: 'container-fluid alert-menu',
    '[attr.type]': '"popup"',
    '[class.active]': 'isOpen()',
  },
})
export class AlertMenuComponent {
  // --- Inputs (signal-based) ---
  isOpen = input<boolean>(false);
  clientId = input<number>(null);

  // --- Outputs ---
  alertCountChanged = output<AlertCountInfo>();

  // --- Internal state (signals for collections) ---
  allAlerts = signal<Array<Alert>>([]);
  alerts = signal<Array<Alert>>([]);
  alertTypes = signal<ExtendedMultiSelectOption[]>([]);
  alertGeographies = signal<ExtendedMultiSelectOption[]>([]);

  // --- Mutable state (used with ngModel) ---
  selectedAlertTypeIds: number[] = [];
  selectedAlertGeographyIds: number[] = [];
  hideReadAlerts = false;
  hideClearedAlerts = false;
  isAlertNotificationListHidden = false;
  selectedAlert: any;
  currentAlertPage = 1;
  scrollableNotificationList: Element;
  alertTypeMultiSelectDisabled = false;
  geographyMultiSelectDisabled = false;

  readonly geographyMultiSelectSettings: IMultiSelectSettings = {
    enableSearch: true,
    checkedStyle: 'fontawesome',
    containerClasses: 'dropdown-block',
    showCheckAll: true,
    showUncheckAll: true,
    dynamicTitleMaxItems: 2,
    displayAllSelectedText: true,
  };
  geographyMultiSelectTexts: IMultiSelectTexts = {
    defaultTitle: 'main.navigation.alerts.geography.default_title',
    allSelected: 'main.navigation.alerts.geography.all_selected',
    checked: 'main.navigation.alerts.geography.geography_checked',
    checkedPlural: 'main.navigation.alerts.geography.geographies_checked',
  };

  readonly alertTypeSelectSettings: IMultiSelectSettings = {
    enableSearch: true,
    checkedStyle: 'fontawesome',
    containerClasses: 'dropdown-block',
    showCheckAll: true,
    showUncheckAll: true,
    dynamicTitleMaxItems: 1,
    displayAllSelectedText: true,
  };
  readonly alertTypeSelectionTexts: IMultiSelectTexts = {
    defaultTitle: 'main.navigation.alerts.type.default_title',
    allSelected: 'main.navigation.alerts.type.all_selected',
    checked: 'main.navigation.alerts.type.checked_singular',
    checkedPlural: 'main.navigation.alerts.type.checked_plural',
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    private headerService: HeaderService,
    private alertNotificationService: AlertNotificationService,
    private authenticationService: AuthenticationService,
    private filterService: ActiveFilterService,
    private router: Router,
    private element: ElementRef,
    private translateService: TranslateService
  ) {
    // React to clientId changes to reload alerts on every client switch
    effect(() => {
      const cid = this.clientId();
      const uid = this.authenticationService._userInfoView?.Id;
      if (cid && uid) {
        this.loadAlerts(uid);
      }
    });
    // Resolve .notification-list once the menu is open and the element is in the DOM
    // Resolve .notification-list once the menu is open and the element is in the DOM
    effect(() => {
      if (this.isOpen()) {
        // Allow the *ngIf content to render before querying
        queueMicrotask(() => {
          this.scrollableNotificationList =
            this.element.nativeElement.querySelector('.notification-list');
        });
      } else {
        this.scrollableNotificationList = null;
      }
    });
  }

  private loadAlerts(userId: number) {
    // https://rouseservices.atlassian.net/browse/RIE-13134
    // If the user doesn't have RDOAccess alerts will get a 403 error.
    const selectedClient =
      this.authenticationService._userInfoView?.SelectedClient;
    if (!selectedClient?.RDOAccess) {
      return;
    }

    this.headerService
      .getAlerts(userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((a) => {
        const allAlerts = (<any>a) as Array<Alert>;
        this.allAlerts.set(allAlerts);

        const alertTypeCountMap = _.countBy(allAlerts, 'AlertTypeName');
        const geographyCountMap = _.countBy(allAlerts, 'Geography');

        const alertTypesRaw = _.sortBy(
          _.uniqBy(allAlerts, 'AlertTypeName'),
          'AlertTypeName'
        ).map((alert, index) => ({
          id: index,
          realName: alert.AlertTypeName,
          name:
            alert.AlertTypeName +
            ' (' +
            (alertTypeCountMap[alert.AlertTypeName] || 0) +
            ')',
        }));

        if (alertTypesRaw.length === 1) {
          this.alertTypeMultiSelectDisabled = true;
          alertTypesRaw.push({ id: -1, name: 'dummy', realName: 'dummy' });
          this.selectedAlertTypeIds = [alertTypesRaw[0].id];
        }
        this.alertTypes.set(alertTypesRaw);

        const geographiesRaw = _.sortBy(
          _.uniqBy(allAlerts, 'Geography'),
          'Geography'
        ).map((alert, index) => ({
          id: index,
          realName: alert.Geography,
          name:
            alert.Geography +
            ' (' +
            (geographyCountMap[alert.Geography] || 0) +
            ')',
        }));

        if (geographiesRaw.length === 1) {
          this.geographyMultiSelectDisabled = true;
          geographiesRaw.push({ id: -1, name: 'dummy', realName: 'dummy' });
          this.selectedAlertGeographyIds = [geographiesRaw[0].id];
        }
        this.alertGeographies.set(geographiesRaw);

        this.alerts.set(_.take(allAlerts, 20));
        this.emitAlertCount();
      });
  }

  private emitAlertCount() {
    const all = this.allAlerts();
    const alertCount = _.filter(
      all,
      (alert) => alert.AlertStatus !== AlertStatus.Read
    ).length;
    const alertReadCount = _.filter(
      all,
      (alert) => alert.AlertStatus === AlertStatus.Read
    ).length;
    this.alertCountChanged.emit({
      alertCount,
      alertReadCount,
      totalAlerts: all.length,
    });
  }

  getUnresolvedForText(NumPeriodNotCleared: number) {
    const translation = this.translateService.instant(
      'main.navigation.alerts.unresolved_for_X_periods'
    );
    if (translation) {
      return translation.replace('%X%', NumPeriodNotCleared);
    } else {
      return NumPeriodNotCleared;
    }
  }

  getPaginatedAlerts = () => {
    this.currentAlertPage++;
    const perPage = 20;
    const offset = (this.currentAlertPage - 1) * perPage;
    let filteredAlerts = this.getAlertsWithGeographyFilter(this.allAlerts());
    filteredAlerts = this.getAlertsWithAlertTypeFilter(filteredAlerts);

    let nextAlerts = [];
    if (this.hideReadAlerts && this.hideClearedAlerts) {
      nextAlerts = _.drop(
        _.filter(
          filteredAlerts,
          (alert) => alert.AlertStatus !== AlertStatus.Read && !alert.IsCleared
        ),
        offset
      ).slice(0, perPage);
    } else if (this.hideReadAlerts) {
      nextAlerts = _.drop(
        _.filter(
          filteredAlerts,
          (alert) => alert.AlertStatus !== AlertStatus.Read
        ),
        offset
      ).slice(0, perPage);
    } else if (this.hideClearedAlerts) {
      nextAlerts = _.drop(
        _.filter(filteredAlerts, (alert) => !alert.IsCleared),
        offset
      ).slice(0, perPage);
    } else {
      nextAlerts = _.drop(filteredAlerts, offset).slice(0, perPage);
    }
    if (nextAlerts && nextAlerts.length) {
      this.alerts.set(_.union(this.alerts(), nextAlerts));
    }
  };

  alertCriteriaChange = (loseFocus: boolean = false) => {
    let filteredAlerts = this.getAlertsWithGeographyFilter(this.allAlerts());
    filteredAlerts = this.getAlertsWithAlertTypeFilter(filteredAlerts);

    if (this.hideReadAlerts && this.hideClearedAlerts) {
      this.alerts.set(
        _.take(
          _.filter(
            filteredAlerts,
            (alert) =>
              alert.AlertStatus !== AlertStatus.Read && !alert.IsCleared
          ),
          20
        )
      );
    } else if (this.hideReadAlerts) {
      this.alerts.set(
        _.take(
          _.filter(
            filteredAlerts,
            (alert) => alert.AlertStatus !== AlertStatus.Read
          ),
          20
        )
      );
    } else if (this.hideClearedAlerts) {
      this.alerts.set(
        _.take(
          _.filter(filteredAlerts, (alert) => !alert.IsCleared),
          20
        )
      );
    } else {
      this.alerts.set(_.take(filteredAlerts, 20));
    }
    this.setAlertNotificationListHidden();
    this.currentAlertPage = 1;
    const notificationList =
      this.element.nativeElement.querySelector('.notification-list');
    if (notificationList) {
      notificationList.scrollTop = 0;
    }
    if (loseFocus) {
      $('#alert-checkboxes').click();
    }
  };

  getAlertsWithGeographyFilter = (allAlerts: Array<Alert>): Array<Alert> => {
    if (this.selectedAlertGeographyIds.length) {
      const selectedGeographies: string[] = [];
      this.selectedAlertGeographyIds.forEach((alertGeographyId) => {
        const found = _.find(this.alertGeographies(), { id: alertGeographyId });
        if (found) {
          selectedGeographies.push(found.realName);
        }
      });
      return allAlerts.filter(
        (alert) => selectedGeographies.indexOf(alert.Geography) !== -1
      );
    }
    return allAlerts;
  };

  getAlertsWithAlertTypeFilter = (allAlerts: Array<Alert>): Array<Alert> => {
    if (this.selectedAlertTypeIds.length) {
      const selectedAlertTypes: string[] = [];
      this.selectedAlertTypeIds.forEach((alertTypeId) => {
        const found = _.find(this.alertTypes(), { id: alertTypeId });
        if (found) {
          selectedAlertTypes.push(found.realName);
        }
      });
      return allAlerts.filter(
        (alert) => selectedAlertTypes.indexOf(alert.AlertTypeName) !== -1
      );
    }
    return allAlerts;
  };

  setAlertNotificationListHidden = () => {
    const all = this.allAlerts();
    if (this.hideReadAlerts && this.hideClearedAlerts) {
      let filteredAlerts = this.getAlertsWithGeographyFilter(all);
      filteredAlerts = this.getAlertsWithAlertTypeFilter(filteredAlerts);
      const count = _.filter(
        filteredAlerts,
        (alert) => alert.AlertStatus === AlertStatus.Read || alert.IsCleared
      ).length;
      this.isAlertNotificationListHidden = count === filteredAlerts.length;
    } else if (this.hideReadAlerts) {
      let filteredAlerts = this.getAlertsWithGeographyFilter(all);
      filteredAlerts = this.getAlertsWithAlertTypeFilter(filteredAlerts);
      const count = _.filter(
        filteredAlerts,
        (alert) => alert.AlertStatus === AlertStatus.Read
      ).length;
      this.isAlertNotificationListHidden = count === filteredAlerts.length;
    } else if (this.hideClearedAlerts) {
      let filteredAlerts = this.getAlertsWithGeographyFilter(all);
      filteredAlerts = this.getAlertsWithAlertTypeFilter(filteredAlerts);
      const count = _.filter(filteredAlerts, (alert) => alert.IsCleared).length;
      this.isAlertNotificationListHidden = count === filteredAlerts.length;
    } else {
      this.isAlertNotificationListHidden = this.alerts().length === 0;
    }
  };

  handleAlertClick = (index: number, alert: Alert) => {
    $('#alert-icon' + index).tooltip('hide');
    this.selectedAlert = alert;
    const body = {
      UserID: this.authenticationService._userInfoView.Id,
      AlertId: alert.AlertID,
      AlertStatus: AlertStatus.Read,
    };
    this.headerService
      .updateAlert(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        alert.AlertStatus = AlertStatus.Read;
        this.emitAlertCount();
        this.alertNotificationService.alertSelected(alert);
        this.router.navigateByUrl(
          this.getProductTypeUrlTree(
            alert.Category,
            alert.UseRouseSchema
              ? alert.RouseProductType
              : alert.ClientProductType,
            alert.Geography,
            alert.AlertID.toString()
          )
        );
        setTimeout(() => {
          this.filterService.clearFiltersSubject.next(['Outlier', 'Verticals']);
        }, 0);
      });
  };

  private getProductTypeUrlTree = (
    category: string,
    productType: string,
    geography: string | number = null,
    alertId: string = null
  ): UrlTree => {
    const params: any = { category };
    if (geography) {
      params.geography = geography;
    }
    if (alertId) {
      params.alert = alertId;
    }
    return this.router.createUrlTree([
      '/equipment',
      params,
      'product-types',
      productType,
      params,
    ]);
  };
}
